'use client';

/**
 * Filterpanelen för /sok och katalogsidorna.
 *
 * Ombyggd 2026-09-15. Den gamla kunde bara ett val per kategori, saknade antal
 * på allt utom märke, och prisfältet visade mängdens min och max som
 * platshållare — vilket såg ut som trasiga värden ("23 – 2") snarare än som en
 * upplysning om spannet.
 *
 * Vad som ändrats:
 *   · flerval på märke och kvalitet, kommaseparerat i adressen
 *   · antal på varje val, så ingen klickar sig till noll träffar
 *   · pris med färdiga spann byggda på kvartilerna i just den här mängden
 *   · "finns hemma" och "hämtas i Helsingborg" som egna snabbval
 *   · sortering
 *   · aktiva filter som chips man tar bort ett och ett
 *
 * Länkar, inte knappar: klienten navigerar ändå utan omladdning, och en länk går
 * att öppna i ny flik, nås med tangentbord och läses av skärmläsare.
 * scroll={false} håller kvar kundens plats i träfflistan.
 */

import { useEffect, useState } from 'react';
import { Link } from '@/components/LocaleLink';
import { useRouter } from 'next/navigation';
import {
  SlidersHorizontal, X, Check, Tag, Building2, ShieldCheck, Wallet,
  ArrowUpDown, PackageCheck, Ruler, ChevronDown,
} from 'lucide-react';
import { useLocale } from '@/hooks/useLocale';
import { t } from '@/i18n/t';

export interface Facet { id: number; name: string; slug?: string; count: number }
export interface SearchFacets {
  categories: Facet[];
  manufacturers: Facet[];
  price: { min: number; max: number; p25?: number; p50?: number; p75?: number } | null;
  quality?: { original: number; aftermarket: number } | null;
  stock?: { express: number; inStock: number } | null;
  /** Artikelkriterier ur TecDoc: Position, Bromsskivetyp, Gängmått … */
  attributes?: Array<{ name: string; values: Array<{ value: string; count: number }> }>;
  total: number;
}

export interface FilterState {
  q?: string;
  ktype?: string;
  quality?: string;       // 'original' | 'aftermarket' | båda kommaseparerat
  express?: string;       // '1'
  inStock?: string;       // '1'
  sort?: string;
  category?: string;
  manufacturer?: string;  // /sok: delmärken (flera) · katalog: bilmärket, bevaras
  brand?: string;         // katalog: delmärken (flera)
  minPrice?: string;
  maxPrice?: string;
  /** Upprepad parameter: attr=Position:Framaxel&attr=Position:Bakaxel */
  attr?: string[];
}

const SORTERINGAR = [
  { varde: '', etikett: 'Mest relevant' },
  { varde: 'lager', etikett: 'På hyllan i Helsingborg först' },
  { varde: 'pris_lagst', etikett: 'Lägsta pris' },
  { varde: 'pris_hogst', etikett: 'Högsta pris' },
  { varde: 'namn', etikett: 'Namn A–Ö' },
] as const;

/** "12,44" → ['12','44'] */
const tillLista = (v: string | undefined): string[] =>
  (v ?? '').split(',').map((s) => s.trim()).filter(Boolean);

/** Lägger till eller tar bort ett värde ur en kommaseparerad lista. */
const vaxla = (v: string | undefined, varde: string): string | undefined => {
  const lista = tillLista(v);
  const ny = lista.includes(varde) ? lista.filter((x) => x !== varde) : [...lista, varde];
  return ny.length ? ny.join(',') : undefined;
};

const kr = (n: number) => `${Math.round(n).toLocaleString('sv-SE')} kr`;

/** Lägger till eller tar bort "Namn:Värde" ur listan av kriterievalen. */
const vaxlaAttr = (lista: string[] | undefined, post: string): string[] => {
  const nu = lista ?? [];
  return nu.includes(post) ? nu.filter((x) => x !== post) : [...nu, post];
};

/*
 * Section och Val ligger på modulnivå. De var förut definierade INNE i
 * SearchFilters, vilket ger nya komponentidentiteter vid varje rendering:
 * React monterade då om hela panelen så fort något state ändrades — prisfältet
 * tappade fokus efter varje tecken, och sektioner man fällt ihop öppnades igen
 * (ägaren 2026-09-16: "varför kollapsar alla flikar").
 */
function Section({ icon: Icon, title, valda = 0, children }: {
    icon: typeof Tag; title: string; valda?: number; children: React.ReactNode;
  }) {
  // Öppen från start (ägaren 2026-09-16, med Thansen som förebild): valen
  // ska synas direkt, inte ligga bakom en pil. Panelen scrollar internt.
  return (
    <details open className="group border-b last:border-b-0">
      <summary className="flex items-center gap-2 text-sm font-semibold py-3.5 cursor-pointer list-none select-none hover:text-primary transition-colors">
        <Icon className="h-4 w-4 text-primary shrink-0" />
        <span className="flex-1 min-w-0 truncate">{title}</span>
        {valda > 0 && (
          <span className="h-5 min-w-5 px-1 rounded-full bg-primary text-primary-foreground text-[11px] font-bold flex items-center justify-center shrink-0">
            {valda}
          </span>
        )}
        <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0 transition-transform group-open:rotate-180" />
      </summary>
      <div className="pb-4">{children}</div>
    </details>
  );
}

  /** En rad med kryssruta. Antalet står alltid ut, även noll — det är information. */
function Val({ label, count, active, href, kryss = true, onNavigate }: {
    label: string; count?: number; active: boolean; href: string; kryss?: boolean; onNavigate?: () => void;
  }) {
    const tomt = count === 0;
    const inner = (
      <>
        <span className="flex items-center gap-2 min-w-0">
          {kryss && (
            <span className={`h-4 w-4 shrink-0 rounded border flex items-center justify-center transition-colors ${
              active ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/40'
            }`}>
              {active && <Check className="h-3 w-3" strokeWidth={3} />}
            </span>
          )}
          {/* title: namnen kapas i en 240 px bred spalt ("Varningssenso…").
              Utan det går de inte att läsa alls. */}
          <span className="truncate" title={label}>{label}</span>
        </span>
        {typeof count === 'number' && (
          <span className="text-xs text-muted-foreground shrink-0 tabular-nums">{count.toLocaleString('sv-SE')}</span>
        )}
      </>
    );
    const klass = `flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
      active ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-accent text-foreground'
    }`;
    // Ett val som ger noll träffar ska synas men inte gå att klicka på.
    if (tomt && !active) {
      return <span className={`${klass} opacity-40 cursor-not-allowed`} aria-disabled="true">{inner}</span>;
    }
    return <Link href={href} scroll={false} onClick={onNavigate} className={klass}>{inner}</Link>;
}

/** Prisreglage med två handtag. Två <input type=range> ovanpå varandra; bara
 *  handtagen tar pekare (se .af-reglage i globals.css). */
function PrisReglage({ min, max, fran, till, onCommit }: {
  min: number; max: number; fran?: number; till?: number;
  onCommit: (lo: number | undefined, hi: number | undefined) => void;
}) {
  const [lo, setLo] = useState(fran ?? min);
  const [hi, setHi] = useState(till ?? max);
  useEffect(() => { setLo(fran ?? min); setHi(till ?? max); }, [fran, till, min, max]);
  if (max <= min) return null;
  const steg = Math.max(1, Math.round((max - min) / 200));
  const pct = (v: number) => ((v - min) / (max - min)) * 100;
  const commit = () => onCommit(lo <= min ? undefined : lo, hi >= max ? undefined : hi);
  return (
    <div className="px-2 pt-1 pb-2">
      <div className="af-reglage relative h-7">
        <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-1.5 rounded-full bg-muted" />
        <div className="absolute top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-primary" style={{ left: `${pct(lo)}%`, right: `${100 - pct(hi)}%` }} />
        <input type="range" min={min} max={max} step={steg} value={lo}
          onChange={(e) => setLo(Math.min(Number(e.target.value), hi - steg))}
          onPointerUp={commit} onKeyUp={commit} onTouchEnd={commit}
          aria-label="Lägsta pris" />
        <input type="range" min={min} max={max} step={steg} value={hi}
          onChange={(e) => setHi(Math.max(Number(e.target.value), lo + steg))}
          onPointerUp={commit} onKeyUp={commit} onTouchEnd={commit}
          aria-label="Högsta pris" />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground tabular-nums -mt-1">
        <span>{kr(lo)}</span><span>{kr(hi)}</span>
      </div>
    </div>
  );
}

export function SearchFilters({
  facets,
  current,
  basePath = '/sok',
  brandKey = 'manufacturer',
  showCategories = true,
  sticky = false,
  before,
}: {
  facets: SearchFacets;
  current: FilterState;
  basePath?: string;
  brandKey?: 'manufacturer' | 'brand';
  showCategories?: boolean;
  /** Fäst panelen under sidhuvudet (och kategorichipsen) när sidan scrollas. */
  sticky?: boolean;
  /** Eget block överst i panelen, t.ex. fordonssidans kategoriträd. */
  before?: React.ReactNode;
}) {
  const { locale } = useLocale();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const stang = () => setOpen(false);
  const [minP, setMinP] = useState(current.minPrice ?? '');
  const [maxP, setMaxP] = useState(current.maxPrice ?? '');
  useEffect(() => { setMinP(current.minPrice ?? ''); setMaxP(current.maxPrice ?? ''); }, [current.minPrice, current.maxPrice]);

  const url = (overrides: Partial<FilterState>): string => {
    const m = { ...current, ...overrides };
    const p = new URLSearchParams();
    for (const nyckel of ['q', 'ktype', 'quality', 'express', 'inStock', 'sort',
                          'category', 'manufacturer', 'brand', 'minPrice', 'maxPrice'] as const) {
      const v = m[nyckel];
      if (v) p.set(nyckel, String(v));
    }
    // Kriterierna är flera värden på samma nyckel, inte ett kommaseparerat fält —
    // värdena innehåller själva komma ("M 20 x 1,5") och hade blivit oläsbara.
    for (const a of m.attr ?? []) p.append('attr', a);
    const qs = p.toString();
    return qs ? `${basePath}?${qs}` : basePath;
  };

  const brandValues = tillLista(current[brandKey]);
  const qualityValues = tillLista(current.quality);

  const rensaAllt = url({
    quality: undefined, express: undefined, inStock: undefined, category: undefined,
    brand: undefined, minPrice: undefined, maxPrice: undefined, attr: [],
    ...(brandKey === 'manufacturer' ? { manufacturer: undefined } : {}),
  });

  const anvandPris = () => {
    setOpen(false);
    router.push(url({ minPrice: minP.trim() || undefined, maxPrice: maxP.trim() || undefined }), { scroll: false });
  };

  const aktiva = [
    ...(showCategories && current.category
      ? [{ etikett: facets.categories.find((c) => String(c.id) === current.category)?.name ?? 'Kategori',
           href: url({ category: undefined }) }]
      : []),
    ...brandValues.map((id) => ({
      etikett: facets.manufacturers.find((m) => String(m.id) === id)?.name ?? id,
      href: url({ [brandKey]: vaxla(current[brandKey], id) }),
    })),
    ...qualityValues.map((q) => ({
      etikett: q === 'original' ? 'Original (OEM)' : t(locale, 'searchPage.aftermarket'),
      href: url({ quality: vaxla(current.quality, q) }),
    })),
    ...(current.express === '1' ? [{ etikett: t(locale, 'filters.onShelf'), href: url({ express: undefined }) }] : []),
    ...(current.inStock === '1' ? [{ etikett: t(locale, 'filters.supplierStock'), href: url({ inStock: undefined }) }] : []),
    ...(current.attr ?? []).map((a) => ({
      etikett: a.slice(a.indexOf(':') + 1),
      href: url({ attr: vaxlaAttr(current.attr, a) }),
    })),
    ...(current.minPrice || current.maxPrice
      ? [{ etikett: `${current.minPrice ? kr(Number(current.minPrice)) : 'Från 0 kr'} – ${current.maxPrice ? kr(Number(current.maxPrice)) : 'uppåt'}`,
           href: url({ minPrice: undefined, maxPrice: undefined }) }]
      : []),
  ];

  const hasFacets = (showCategories && facets.categories.length > 0)
    || facets.manufacturers.length > 0 || !!facets.price;

  /**
   * Hopfälld som standard. Med kategori, märke, kvalitet, pris och upp till åtta
   * artikelkriterier blev panelen en meterlång vägg där inget gick att överblicka.
   * Ägaren 2026-09-15: "det är kanske inte det någon vill filtrera mot direkt".
   *
   * Undantaget är en sektion som redan har ett val — den öppnas, så kunden alltid
   * ser vad som är valt utan att leta. <details> i stället för egen state: det
   * fungerar utan JavaScript, går att nå med tangentbord och läses av skärmläsare.
   */


  const prisSpann = (() => {
    const p = facets.price;
    if (!p) return [];
    const { min, max, p25, p50, p75 } = p;
    // Kvartilerna ger spann som faktiskt delar just den här mängden i fyra delar.
    // Fasta trappor (0–500, 500–1000 …) hamnar ofta så att allt ligger i ett spann.
    const punkter = [p25, p50, p75].filter((n): n is number => typeof n === 'number' && n > min && n < max);
    if (punkter.length < 3) return [];
    const kanter = [Math.floor(min), ...punkter, Math.ceil(max)];
    return kanter.slice(0, -1).map((fran, i) => ({
      fran, till: kanter[i + 1]!,
      etikett: i === 0 ? `Under ${kr(kanter[1]!)}`
        : i === kanter.length - 2 ? `Över ${kr(fran)}`
        : `${kr(fran)} – ${kr(kanter[i + 1]!)}`,
    }));
  })();

  const content = (
    <div className="text-sm">
      <div className="flex items-center justify-between pb-3 border-b">
        <span className="font-semibold flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-primary" />
          {t(locale, 'filters.title')}
        </span>
        {aktiva.length > 0 && (
          <Link href={rensaAllt} scroll={false} onClick={() => setOpen(false)} className="text-xs text-primary hover:underline">
            {t(locale, 'filters.clearAll')}
          </Link>
        )}
      </div>

      {/* Aktiva filter — kunden ska se vad som är valt utan att läsa igenom listorna */}
      {aktiva.length > 0 && (
        <div className="flex flex-wrap gap-1.5 py-3 border-b">
          {aktiva.map((a, i) => (
            <Link key={i} href={a.href} scroll={false} onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors">
              {a.etikett}
              <X className="h-3 w-3" />
            </Link>
          ))}
        </div>
      )}

      {before}

      {/* Sortering */}
      <Section icon={ArrowUpDown} title="Sortering" valda={current.sort ? 1 : 0}>
        <div className="space-y-0.5">
          {SORTERINGAR.map((s) => (
            <Val onNavigate={stang} key={s.varde || 'standard'} label={s.etikett} kryss={false}
              active={(current.sort ?? '') === s.varde}
              href={url({ sort: s.varde || undefined })} />
          ))}
        </div>
      </Section>

      {/* Pris — högt upp, direkt efter sorteringen (ägaren 2026-09-16, Thansen som förebild). */}
      {facets.price && (
        <Section icon={Wallet} title={t(locale, 'filters.priceRange')}
          valda={current.minPrice || current.maxPrice ? 1 : 0}>
          <p className="text-xs text-muted-foreground mb-2">
            Spannet här: {kr(facets.price.min)} – {kr(facets.price.max)}
          </p>
          {prisSpann.length > 0 && (
            <div className="space-y-0.5 mb-2">
              {prisSpann.map((s, i) => {
                const active = current.minPrice === String(s.fran) && current.maxPrice === String(s.till);
                return (
                  <Val onNavigate={stang} key={i} label={s.etikett} active={active}
                    href={url(active
                      ? { minPrice: undefined, maxPrice: undefined }
                      : { minPrice: String(s.fran), maxPrice: String(s.till) })} />
                );
              })}
            </div>
          )}
          {/* Dra från båda håll, som hos Thansen (ägaren 2026-09-16). Släpper man
              handtaget verkställs valet; fälten under tar Enter eller blur. */}
          <PrisReglage
            min={Math.floor(facets.price.min)} max={Math.ceil(facets.price.max)}
            fran={current.minPrice ? Number(current.minPrice) : undefined}
            till={current.maxPrice ? Number(current.maxPrice) : undefined}
            onCommit={(lo, hi) => {
              setOpen(false);
              router.push(url({ minPrice: lo === undefined ? undefined : String(lo), maxPrice: hi === undefined ? undefined : String(hi) }), { scroll: false });
            }}
          />
          <div className="flex items-center gap-2 mt-1">
            <input type="number" inputMode="numeric" min={0} value={minP}
              onChange={(e) => setMinP(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') anvandPris(); }} onBlur={() => { if ((minP.trim() || '') !== (current.minPrice ?? '')) anvandPris(); }}
              placeholder="Från"
              className="w-full min-w-0 px-2 py-1.5 text-sm border rounded-md bg-background" aria-label={t(locale, 'filters.minPrice')} />
            <span className="text-muted-foreground">–</span>
            <input type="number" inputMode="numeric" min={0} value={maxP}
              onChange={(e) => setMaxP(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') anvandPris(); }} onBlur={() => { if ((maxP.trim() || '') !== (current.maxPrice ?? '')) anvandPris(); }}
              placeholder="Till"
              className="w-full min-w-0 px-2 py-1.5 text-sm border rounded-md bg-background" aria-label={t(locale, 'filters.maxPrice')} />
          </div>
        </Section>
      )}

      {/* Tillgänglighet.
          Två helt olika saker, och de får inte heta samma. `quantity > 0` betyder
          att LEVERANTÖREN har delen — 321 812 produkter, som hämtas hem via
          centrallagret på några dagar. store_inventory betyder att den står på
          VÅR hylla i Helsingborg — 1 544 produkter, som kunden kan hämta i dag.
          Etiketten hette tidigare "Finns hemma" för det första fallet, vilket
          lovade butikslager för nästan hela katalogen. Rättat 2026-09-15. */}
      {facets.stock && (
        <Section icon={PackageCheck} title={t(locale, 'filters.availability')}
          valda={(current.express === '1' ? 1 : 0) + (current.inStock === '1' ? 1 : 0)}>
          <div className="space-y-0.5">
            <Val onNavigate={stang} label={t(locale, 'filters.onShelf')} count={facets.stock.express}
              active={current.express === '1'}
              href={url({ express: current.express === '1' ? undefined : '1' })} />
            <p className="px-2 pb-1 text-xs text-muted-foreground">{t(locale, 'filters.onShelfHelp')}</p>
            <Val onNavigate={stang} label={t(locale, 'filters.supplierStock')} count={facets.stock.inStock}
              active={current.inStock === '1'}
              href={url({ inStock: current.inStock === '1' ? undefined : '1' })} />
            <p className="px-2 text-xs text-muted-foreground">{t(locale, 'filters.supplierStockHelp')}</p>
          </div>
        </Section>
      )}

      {/* Kategori (bara i sökläge) */}
      {showCategories && facets.categories.length > 0 && (
        <Section icon={Tag} title={t(locale, 'filters.category')} valda={current.category ? 1 : 0}>
          <div className="space-y-0.5 max-h-72 overflow-y-auto pr-1">
            {facets.categories.map((c) => {
              const active = current.category === String(c.id);
              return (
                <Val onNavigate={stang} key={c.id} label={c.name} count={c.count} active={active}
                  href={url({ category: active ? undefined : String(c.id) })} />
              );
            })}
          </div>
        </Section>
      )}

      {/* Märke — flerval */}
      {facets.manufacturers.length > 0 && (
        <Section icon={Building2} title={t(locale, 'filters.brand')} valda={brandValues.length}>
          <div className="space-y-0.5 max-h-72 overflow-y-auto pr-1">
            {facets.manufacturers.map((m) => (
              <Val onNavigate={stang} key={m.id} label={m.name} count={m.count}
                active={brandValues.includes(String(m.id))}
                href={url({ [brandKey]: vaxla(current[brandKey], String(m.id)) })} />
            ))}
          </div>
        </Section>
      )}

      {/* Kvalitet — flerval, med antal */}
      <Section icon={ShieldCheck} title={t(locale, 'filters.quality')} valda={qualityValues.length}>
        <div className="space-y-0.5">
          <Val onNavigate={stang} label="Original (OEM)" count={facets.quality?.original}
            active={qualityValues.includes('original')}
            href={url({ quality: vaxla(current.quality, 'original') })} />
          <Val onNavigate={stang} label={t(locale, 'searchPage.aftermarket')} count={facets.quality?.aftermarket}
            active={qualityValues.includes('aftermarket')}
            href={url({ quality: vaxla(current.quality, 'aftermarket') })} />
        </div>
      </Section>

      {/* Artikelkriterier — samma sorts filter som leverantörernas egna kataloger.
          Vilka som visas beror på träfflistan: bromsbelägg ger Position och
          Slitvarnarkontakt, kylare ger andra. */}
      {(facets.attributes ?? []).map((a) => (
        <Section key={a.name} icon={Ruler} title={a.name}
          valda={(current.attr ?? []).filter((x) => x.startsWith(`${a.name}:`)).length}>
          <div className="space-y-0.5 max-h-60 overflow-y-auto pr-1">
            {a.values.map((v) => {
              const post = `${a.name}:${v.value}`;
              return (
                <Val onNavigate={stang} key={v.value} label={v.value} count={v.count}
                  active={(current.attr ?? []).includes(post)}
                  href={url({ attr: vaxlaAttr(current.attr, post) })} />
              );
            })}
          </div>
        </Section>
      ))}

    </div>
  );

  if (!hasFacets) return null;

  return (
    <>
      <aside className={`hidden lg:block w-60 shrink-0 ${sticky ? 'lg:sticky lg:top-[calc(var(--af-header-h,0px)_+_var(--af-chips-h,0px)_+_0.75rem)] lg:max-h-[calc(100vh_-_var(--af-header-h,0px)_-_var(--af-chips-h,0px)_-_1.5rem)] lg:overflow-y-auto' : ''}`}>
        {/* I fäst läge scrollar <aside> — då får inte den inre rutan också göra
            det, annars blir det två scrollister bredvid varandra (ägaren 2026-09-16). */}
        <div className={sticky ? 'rounded-lg border bg-background p-4' : 'sticky top-24 rounded-lg border bg-background p-4 max-h-[calc(100vh-7rem)] overflow-y-auto'}>
          {content}
        </div>
      </aside>

      {/* Filter och sortering på mobil — en rad i flödet, inte en flytande knapp.
          Den var tidigare en rund knapp centrerad ovanför bottennavigeringen, och
          den satt då MITT ÖVER träfflistan: på en 390 px skärm dolde den priset på
          de två första produkterna (skärmdump 2026-09-22). Ägaren: "när man trycker
          på sök på mobilen ingen filter eller något".

          Datorn visar sorteringen uppslagen i sidospalten. Mobilen hade den gömd
          bakom en knapp som både var lätt att missa och täckte det man sökte efter.
          Nu står båda som text, i sidans eget flöde, klistrade under sökfältet så
          de följer med ned genom listan.

          Sorteringen är en <select> med flit — telefonens egen hjullista är både
          snabbare och mer förutsägbar än en egenbyggd meny. */}
      <div className="lg:hidden sticky top-[calc(var(--af-header-h,0px)_+_0.25rem)] z-20 -mx-4 px-4 py-2 bg-background/95 backdrop-blur-sm border-b flex items-center gap-2">
        <button onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border text-sm font-medium active:scale-95 transition-transform">
          <SlidersHorizontal className="h-4 w-4" />
          {t(locale, 'filters.title')}
          {aktiva.length > 0 && (
            <span className="h-5 min-w-5 px-1 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">{aktiva.length}</span>
          )}
        </button>
        <label className="flex-1 min-w-0">
          <span className="sr-only">Sortering</span>
          <select
            value={current.sort ?? ''}
            onChange={(e) => { window.location.href = url({ sort: e.target.value || undefined }); }}
            className="w-full h-9 rounded-md border bg-background px-2 text-sm"
          >
            {SORTERINGAR.map((s) => (
              <option key={s.varde} value={s.varde}>{s.etikett}</option>
            ))}
          </select>
        </label>
      </div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-[200]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-[85%] max-w-sm bg-background shadow-xl flex flex-col">
            <div className="flex items-center justify-between p-4 pb-2 border-b">
              <span className="font-semibold">{t(locale, 'filters.title')}</span>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-accent" aria-label="Stäng">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4">{content}</div>
            {/* Avsluta-knappen: kunden ska se hur många träffar valen ger innan
                panelen stängs, inte gissa. */}
            <div className="p-4 border-t" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
              <button onClick={() => setOpen(false)}
                className="w-full px-4 py-2.5 rounded-md bg-primary text-primary-foreground font-semibold">
                Visa {facets.total.toLocaleString('sv-SE')} produkter
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
